import * as Schema from "effect/Schema";
import { EnvironmentId, ThreadId, TrimmedNonEmptyString } from "./baseSchemas.ts";

const strict = { parseOptions: { onExcessProperty: "error" as const } };
const coordinate = TrimmedNonEmptyString.check(Schema.isMaxLength(256));
const positive = Schema.Number.check(Schema.isInt(), Schema.isGreaterThan(0));
const opening = Schema.String.check(Schema.isPattern(/^[a-f0-9]{32}$/u));
const path = Schema.String.check(
  Schema.isMaxLength(512),
  Schema.makeFilter((value) => {
    try {
      const decoded = decodeURIComponent(value);
      return !/[\\\0?#]/u.test(decoded) && !decoded.includes("..") && !decoded.includes(":");
    } catch {
      return false;
    }
  }),
);

/** The displayed conversation is checked against the socket's binding. Actor,
 * World, grant and remote URLs are never accepted as client arguments. */
export const CanvasDoorConversation = Schema.Struct({
  threadId: ThreadId,
  sessionId: coordinate,
  environmentId: EnvironmentId,
}).annotate(strict);
export const CanvasDoorTarget = Schema.Struct({
  canvasId: Schema.String.check(Schema.isPattern(/^cnv_[a-f0-9]{24}$/u)),
  memberId: coordinate,
  revisionId: coordinate,
  sequence: positive,
}).annotate(strict);
export type CanvasDoorTarget = typeof CanvasDoorTarget.Type;

const inConversation = { conversation: CanvasDoorConversation };
const target = { ...inConversation, target: CanvasDoorTarget };
const boundedValue = Schema.Unknown.check(
  Schema.makeFilter((value) => {
    try {
      return new TextEncoder().encode(JSON.stringify(value)).byteLength <= 256 * 1024;
    } catch {
      return false;
    }
  }),
);
export const CanvasDoorViewRequest = Schema.Union([
  Schema.Struct({ ...target, operation: Schema.Literal("read") }).annotate(strict),
  Schema.Struct({ ...target, operation: Schema.Literal("open-files") }).annotate(strict),
  Schema.Struct({
    ...target,
    operation: Schema.Literal("open-component"),
    nodeId: coordinate,
  }).annotate(strict),
  Schema.Struct({ ...inConversation, operation: Schema.Literal("close"), opening }).annotate(
    strict,
  ),
]);
export const CanvasDoorActionRequest = Schema.Struct({
  ...target,
  operation: Schema.Literals(["action-prepare", "action-approve", "action-commit"]),
  nodeId: coordinate,
  name: coordinate,
  input: boundedValue,
  requestKey: coordinate,
  digest: Schema.optional(Schema.String.check(Schema.isPattern(/^[a-f0-9]{64}$/u))),
}).annotate(strict);
export const CanvasDoorRequest = Schema.Union([CanvasDoorViewRequest, CanvasDoorActionRequest]);
export type CanvasDoorRequest = typeof CanvasDoorRequest.Type;
export const CanvasDoorResourceRequest = Schema.Struct({
  ...inConversation,
  opening,
  path,
  method: Schema.Literals(["GET", "HEAD"]),
  range: Schema.optional(
    Schema.String.check(Schema.isPattern(/^bytes=\d*-\d*$/u), Schema.isMaxLength(64)),
  ),
  ifNoneMatch: Schema.optional(Schema.String.check(Schema.isMaxLength(256))),
}).annotate(strict);
export type CanvasDoorResourceRequest = typeof CanvasDoorResourceRequest.Type;
export const CanvasDoorDataRequest = Schema.Struct({
  ...target,
  state: Schema.optional(
    Schema.Record(coordinate, boundedValue).check(
      Schema.makeFilter(
        (value) =>
          Object.keys(value).length <= 64 &&
          new TextEncoder().encode(JSON.stringify(value)).byteLength <= 256 * 1024,
      ),
    ),
  ),
}).annotate(strict);
export type CanvasDoorDataRequest = typeof CanvasDoorDataRequest.Type;

/** Resources are streamed in bounded pieces over the already private socket.
 * No resource URL or credential reaches the authored frame. */
export const CANVAS_DOOR_CHUNK_BYTES = 64 * 1024;
export const CanvasDoorResourceEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("response"),
    status: Schema.Literals([200, 206, 304, 404, 416]),
    headers: Schema.Record(Schema.String, Schema.String),
  }),
  Schema.Struct({
    kind: Schema.Literal("bytes"),
    base64: Schema.String.check(Schema.isMaxLength(87384)),
  }),
]);
export type CanvasDoorResourceEvent = typeof CanvasDoorResourceEvent.Type;
export const CanvasDoorResult = Schema.Record(Schema.String, Schema.Unknown);
export type CanvasDoorResult = typeof CanvasDoorResult.Type;
